// Icon — thin wrapper over Lucide. Accepts kebab-case names
// (e.g. "folder-kanban"), renders an inline stroked SVG sized in px.

import { icons, type LucideProps } from 'lucide-react';
import { memo } from 'react';

export interface IconProps extends Omit<LucideProps, 'ref'> {
  /** Kebab-case Lucide icon name, e.g. "chevron-left" */
  name: string;
  /** Icon size in px (applied to both width & height). Default 20 */
  size?: number;
}

/** Convert kebab-case to PascalCase to look up the Lucide component map. */
function toPascal(kebab: string): string {
  return kebab
    .split('-')
    .map((seg) => seg.charAt(0).toUpperCase() + seg.slice(1))
    .join('');
}

function Icon({ name, size = 20, strokeWidth = 2, ...rest }: IconProps) {
  const key = toPascal(name) as keyof typeof icons;
  const LucideIcon = icons[key];

  if (!LucideIcon) {
    if (import.meta.env.DEV) {
      console.warn(`[Icon] Unknown Lucide icon: "${name}" (resolved key: "${key}")`);
    }
    return null;
  }

  return <LucideIcon size={size} strokeWidth={strokeWidth} {...rest} />;
}

export default memo(Icon);
